// src/pages/HomePage.tsx
import { ArrowRight, Settings, Database, Target, BarChart } from 'lucide-react'
import ReactGA from 'react-ga4'
import { Link } from 'react-router-dom'
import { BrandName } from '../components/BrandName'  


export default function HomePage() {
  const trackCTAClick = () => {
    ReactGA.event({
      category: 'CTA',
      action: 'click',
      label: 'Contact'
    })
  }

  return (
    <div className="flex flex-col min-h-screen">
    {/* Hero Section */}
<section className="bg-background-light py-20 md:py-24">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="text-center mx-auto max-w-[68ch]">

      {/* Headline */}
      <h1 className="text-3xl md:text-4xl font-normal tracking-tight leading-snug text-text-dark">
        <span className="font-bold">Helping financial institutions and SaaS</span> turn data into decisions and take confident first steps into&nbsp;
        <span className="text-accent-primary">AI</span>
      </h1>

      {/* Tagline */}
      <p className="mt-2 text-lg sm:text-xl font-semibold text-accent-primary">
        Aligning your spokes to the AI revolution
      </p>

      {/* Supporting text (hybrid voice) */}
      <p className="mt-4 text-base sm:text-lg md:leading-relaxed text-text-dark/80">
        At <BrandName />, we help organizations turn data into a competitive advantage. Whether it is building modern data platforms, improving data quality and governance, or guiding the first steps into AI, our work is backed by 18+ years of enterprise data leadership, including partnering with a regional bank to unify 20+ systems into a scalable data platform.
      </p>

      {/* CTAs */}
      <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-5">
        <Link
          to="/contact"
          className="inline-flex items-center justify-center bg-accent-primary text-white px-7 py-4 rounded-lg font-semibold hover:bg-accent-hover transition-all"
          onClick={trackCTAClick}
          aria-label="Schedule a consultation"
        >
          Schedule a Consultation <ArrowRight className="ml-2" />
        </Link>

        <a
          href="#services"
          className="inline-flex items-center justify-center border border-accent-primary text-accent-primary px-8 py-4 rounded-lg font-semibold hover:bg-accent-primary/10 transition-all"
          aria-label="Jump to services section"
        >
          Learn More About Services
        </a>
      </div>

    </div>
  </div>
</section>





{/* Brand Story Section */}
<section className="py-16 md:py-20 bg-background-primary">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    {/* Two-column layout: fixed image column + flexible text column */}
    <div className="grid gap-10 md:gap-12 md:grid-cols-[320px,1fr] items-center">
      
      {/* Logo Side */}
      <div className="flex justify-center md:justify-start">
        <img
          src="/Logo-Inverted.svg"
          alt="True Spoke wheel icon"
          className="w-56 h-56 md:w-72 md:h-72 object-contain"
        />
      </div>

      {/* Text Content Side */}
      <div className="max-w-[65ch]">
        <h2 className="text-2xl md:text-3xl font-bold text-text-primary tracking-tight mb-3">
          Why the name <BrandName />?
        </h2>

        <p className="text-base md:text-lg text-text-secondary leading-relaxed">
          Just as a wheel's true spokes provide strength and stability, your data provides the foundation for AI.
        </p>

        <p className="mt-4 text-base md:text-lg text-text-secondary leading-relaxed">
          At <BrandName />, we align, balance, and optimize your data so your AI initiatives are reliable, effective, and deliver measurable business results.
        </p>
      </div>
    </div>
  </div>
</section>



      {/* Services Section */}
      <section id="services" className="py-20 bg-background-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-14 text-text-primary">
            How We Drive <span className="text-accent-primary">Value</span>
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                icon: Database,
                title: "Modern Data Platforms",
                points: [
                  "Consolidate fragmented systems into one reliable source of truth",
                  "Design for scale, security, and cost control",
                  "Enable fast, trustworthy analytics"
                ]
              },
              {
                icon: BarChart,
                title: "Data Quality & Governance",
                points: [
                  "Improve data accuracy and lineage",
                  "Set clear ownership and access controls",
                  "Meet compliance without slowing teams"
                ]
              },
              {
                icon: Target,
                title: "AI Readiness & Roadmaps",
                points: [
                  "Find high-value AI use cases",
                  "Assess data gaps and risks",
                  "Plan first steps that deliver results"
                ]
              },
              {
                icon: Settings,
                title: "MLOps & AI Delivery",
                points: [
                  "Streamline model deployment and monitoring",
                  "Shorten time from prototype to production",
                  "Prove impact with clear metrics"
                ]
              },
              {
                icon: BarChart, // reuse or swap for a shield/check icon
                title: "Financial Services & SaaS",
                points: [
                  "Deep experience with banks, credit unions, and SaaS",
                  "Unifying 20+ systems into a scalable data lakehouse",
                  "Practical approaches to risk and governance"
                ]
              },
              {
                icon: Target,
                title: "Advisory & Fractional Leadership",
                points: [
                  "Trusted guidance for execs and data leaders",
                  "Vendor-neutral evaluations and buy/build decisions",
                  "Hands-on help when you need it"
                ]
              }
            ].map((service, idx) => (
              <div
                key={idx}
                className="card p-7 hover:transform hover:scale-[1.02] transition-all duration-300"
              >
                <service.icon className="w-10 h-10 text-accent-primary mb-4" />
                <h3 className="text-lg font-semibold mb-3 text-text-primary">
                  {service.title}
                </h3>
                <ul className="space-y-2 text-text-secondary">
                  {service.points.map((p, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="mt-2 block h-1.5 w-1.5 rounded-full bg-accent-primary/70" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Not sure where to start CTA */}
          <div className="text-center mt-12">
            <p className="text-text-secondary mb-4">
              Not sure where to start? I can help you scope a small first step.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center bg-accent-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-accent-hover transition-all"
              onClick={trackCTAClick}
            >
              Schedule a 20-minute intro
            </Link>
          </div>
        </div>
      </section>`


      {/* Proven Results Section */}
      <section className="py-20 bg-background-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12 text-text-primary">
            Proven <span className="text-accent-primary">Results</span>
          </h2>

          <div className="grid gap-6 md:gap-8 md:grid-cols-3">
            {[
              {
                metric: "80+",
                label: "Projects Delivered",
                description:
                  "From small startups to Fortune 100 companies, including banks and SaaS providers"
              },
              {
                metric: "$2.2M+",
                label: "New Revenue Created",
                description:
                  "Helping clients generate significant returns through data and AI initiatives"
              },
              {
                metric: "90%",
                label: "Faster AI Launches",
                description:
                  "Cutting project timelines so clients see value months sooner"
              }
            ].map((r, i) => (
              <div
                key={i}
                className="card text-center p-8 rounded-2xl border border-white/10 bg-white/5 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="text-5xl font-extrabold tracking-tight text-accent-primary">
                  {r.metric}
                </div>
                <div className="mt-1 text-sm uppercase tracking-widest text-white/90">
                  {r.label}
                </div>
                <p className="mt-4 text-text-secondary">{r.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* CTA Section */}
      <section className="bg-background-tertiary py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 text-text-primary">
            Let’s Turn Your Data into a Competitive Advantage
          </h2>
          <p className="text-xl mb-10 max-w-2xl mx-auto text-text-secondary">
            At <BrandName />, I help organizations build modern data platforms, improve data quality, and take confident steps into AI — so you can make better decisions, faster.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center bg-accent-primary text-white px-8 py-4 rounded-lg font-semibold hover:bg-accent-hover transition-all transform hover:scale-105"
            onClick={trackCTAClick}
          >
            Schedule Your Free Consultation <ArrowRight className="ml-2" />
          </Link>
        </div>
      </section>

    </div>
  )
}